import React from "react";
import HttpServices from "../../utils/Http.services";
import { translate, checkTranslation } from "../../utils/useful";
import { Pie, Doughnut, Bar } from "react-chartjs-2";
import { siteTheme } from "../../variables/config";
import { fakeFormsData } from "../../variables/fakeData";
import { normalizeProcessUserType } from '../../utils/processStageAccess';
import {
    getUserWidgetPreviewRecords,
    summarizeProcessRecords,
} from '../../utils/processWidgetData';
import { buildProcessWidgetRequest } from '../../utils/widgetRequests';

const colors = siteTheme.primaryColors

class ProcessChartWidget extends React.Component {
    state = {
        isLoading: true,
        data: [],
        chartData: {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: [],
            }],
        },
        showLegend: false,
        error: null,
    }

    componentDidMount() {
        this.fetchData()
        window.addEventListener('reloadFetchData', this.handleReloadFetchData)
    }

    componentWillUnmount() {
        window.removeEventListener('reloadFetchData', this.handleReloadFetchData)
    }

    handleReloadFetchData = () => {
        this.fetchData()
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.widget !== this.props.widget
            || prevProps.viewerUserType !== this.props.viewerUserType
            || prevProps.userId !== this.props.userId
            || prevProps.useFakeData !== this.props.useFakeData
        ) {
            this.fetchData()
        }
    }

    fetchData = () => {
        const widget = this.props.widget
        if (!widget) return

        this.setState({ isLoading: true, error: null })
        const body = buildProcessWidgetRequest({
            widget,
            variables: this.props.variables,
            userId: this.props.userId,
            defaultFilter: {
                'values.lastFollowUpResult': 'قرارداد',
            },
        })

        if (this.props.useFakeData) {
            this.setFakeData(body)
            return
        }

        HttpServices.request(widget.fetchUrl, body, (fetchResult, fetchError) => {
            if (fetchError) {
                console.error('Error fetching data:', fetchError)
                this.setState({
                    isLoading: false,
                    error: fetchError,
                    data: [],
                })
                return
            }

            const records = fetchResult?.info || []
            this.processData(records)
        })
    }

    setFakeData = (body) => {
        const contractStatus = body?.filter?.['values.lastFollowUpResult']
        const contractRecords = fakeFormsData.filter(record => {
            if (!contractStatus) return true
            return record.values?.lastFollowUpResult === contractStatus
        })
        const records = getUserWidgetPreviewRecords({
            records: contractRecords,
            userType: this.getCurrentUserType(this.props.widget),
            userId: this.props.userId || this.props.user?.info?._id,
            limit: this.props.widget?.previewLimit ?? 10,
        })
        this.processData(records)
    }

    getCurrentUserType = (widget) => {
        return normalizeProcessUserType(
            this.props.viewerUserType
            || this.props.userType
            || this.props.user?.info?.userType
            || widget?.userType
        )
    }

    processData = (records) => {
        const widget = this.props.widget
        const currentUserType = this.getCurrentUserType(widget)
        const allStages = widget.processStages || {}
        const summary = summarizeProcessRecords(records, allStages, currentUserType)
        const stageCounts = summary.counts
        const stageEntries = summary.entries

        // Modern, vibrant color palette with better contrast
        // Modern, vibrant color palette with better contrast and distinct hues
        const colorPalette = [
            '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
            '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
            '#F43F5E', '#EAB308', '#A855F7', '#D946EF', '#0EA5E9',
            '#22C55E', '#64748B', '#84CC16', '#3B82F6', '#146a5d',
            '#1a8c7a', '#20ae97', '#26d0b4', '#6366f1', '#8b5cf6',
            '#a855f7', '#c084fc', '#d8b4fe', '#3b82f6', '#60a5fa',
            '#93c5fd', '#bfdbfe', '#dbeafe', '#ec4899', '#f472b6',
            '#f9a8d4', '#fbcfe8', '#fce7f3', '#f59e0b', '#fbbf24'
        ]

        const labels = []
        const data = []
        const backgroundColor = []
        let colorIndex = 0

        stageEntries.forEach(([key, stage]) => {
            const assignedColor = stage.color || colorPalette[colorIndex % colorPalette.length]
            colorIndex++

            if (stageCounts[key] > 0) {
                labels.push(checkTranslation(stage.name))
                data.push(stageCounts[key])
                backgroundColor.push(assignedColor)
            }
        })

        this.setState({
            data: records,
            chartData: {
                labels,
                datasets: [{
                    data,
                    backgroundColor,
                }],
            },
            isLoading: false,
            error: null,
        })
    }

    render() {
        const { chartData, isLoading, error } = this.state
        const chartType = this.props.widget.chartType || 'pie'

        if (isLoading) {
            return (
                <div className="w-100 py-3 text-center">
                    <p>{translate('Loading...')}</p>
                </div>
            )
        }

        if (error) {
            return (
                <div className="w-100 py-3 text-center" role="alert">
                    <p style={{ color: '#b42318' }}>{translate('Unable to load dashboard data')}</p>
                </div>
            )
        }

        if (chartData.labels.length === 0) {
            return (
                <div className="w-100 py-3 text-center">
                    <p>{translate('No data available')}</p>
                </div>
            )
        }

        const ChartComponent = chartType === 'bar' ? Bar : (chartType === 'doughnut' ? Doughnut : Pie)

        return (
            <div className="w-100 py-2">
                <div className="d-flex justify-content-end mb-2">
                    <button
                        onClick={() => this.setState({ showLegend: !this.state.showLegend })}
                        style={{
                            background: this.state.showLegend
                                ? `linear-gradient(135deg, ${colors.main1} 0%, ${colors.main1} 75%, ${colors.main2} 100%)`
                                : `linear-gradient(135deg, ${colors.main3}50, ${colors.main3}60)`,
                            border: `2px solid ${this.state.showLegend ? colors.main1 : colors.main3 + '80'}`,
                            borderRadius: 20,
                            padding: '6px 16px',
                            fontSize: 11,
                            fontWeight: 600,
                            color: this.state.showLegend ? '#fff' : colors.main1,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: this.state.showLegend ? `0 2px 8px ${colors.main1}30` : 'none'
                        }}
                    >
                        {this.state.showLegend ? translate('Hide Legend') : translate('Show Legend')}
                    </button>
                </div>
                <div style={{ padding: '20px', overflow: 'hidden', position: 'relative' }}>
                    <div className="p-3" style={{
                        background: '#fff',
                        minHeight: 400,
                        borderRadius: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        overflow: 'visible'
                    }}>
                        <ChartComponent
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                cutout: chartType === 'doughnut' ? '25%' : undefined,
                                layout: {
                                    padding: {
                                        left: 0,
                                        right: 0,
                                        top: 0,
                                        bottom: this.state.showLegend ? 50 : 0
                                    }
                                },
                                plugins: {
                                    legend: {
                                        display: this.state.showLegend,
                                        position: 'bottom',
                                        align: 'center',
                                        labels: {
                                            boxWidth: 14,
                                            padding: 10,
                                            font: { size: 11, weight: '500' },
                                            usePointStyle: true,
                                            pointStyle: 'circle',
                                            generateLabels: function (chart) {
                                                const data = chart.data
                                                if (data.labels.length && data.datasets.length) {
                                                    return data.labels.map((label, i) => {
                                                        const value = data.datasets[0].data[i]
                                                            const color = data.datasets[0].backgroundColor[i]
                                                        const total = data.datasets[0].data.reduce((a, b) => a + b, 0)
                                                        const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                        return {
                                                            text: `${label}: ${value} (${percentage}%)`,
                                                            fillStyle: color,
                                                            strokeStyle: color,
                                                            lineWidth: 0,
                                                            hidden: false,
                                                            index: i
                                                        }
                                                    })
                                                }
                                                return []
                                            }
                                        }
                                    },
                                    datalabels: {
                                        display: false
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context) {
                                                const label = context.label || ''
                                                const value = context.parsed || context.raw
                                                const total = context.dataset.data.reduce((a, b) => a + b, 0)
                                                const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                return `${label}: ${value} (${percentage}%)`
                                            }
                                        }
                                    }
                                },
                                elements: {
                                    arc: {
                                        borderWidth: 2,
                                        borderColor: '#fff'
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        )
    }
}

export default ProcessChartWidget
